import {
  renderTimeViewClock,
  TimePicker,
  TimePickerProps,
} from '@mui/x-date-pickers-pro';
import { forwardRef } from 'react';

import { dateTimeToDate, dateToDateTime } from 'helpers/datetime';

import TimezoneTextField from './components/TimezoneTextField';

import type { TextFieldProps } from '@mui/material';
import type { DateTime } from 'luxon';

type OmittedTimePickerProps =
  | 'components'
  | 'componentsProps'
  | 'localeText'
  | 'slotProps'
  | 'slots'
  | 'viewRenderers';

interface LocatedTimePickerProps extends Omit<
  TimePickerProps<Date>,
  OmittedTimePickerProps
> {
  showCurrentTimeZone?: boolean;
  TextFieldProps?: TextFieldProps;
  timezone?: string;
}

const LocatedTimePicker = forwardRef<HTMLDivElement, LocatedTimePickerProps>(
  (
    {
      showCurrentTimeZone = true,
      TextFieldProps,
      timezone,
      ...props
    }: LocatedTimePickerProps,
    ref,
  ) => {
    /**
     * Convert between Date and DateTime props due to the usage of Luxon DateTime in the
     * MUI TimePicker component
     */
    const convertedTimePickerProps: Omit<
      TimePickerProps<DateTime>,
      OmittedTimePickerProps
    > = {
      ...props,
      defaultValue:
        props.defaultValue && dateToDateTime(props.defaultValue, { timezone }),
      maxTime: props.maxTime && dateToDateTime(props.maxTime, { timezone }),
      minTime: props.minTime && dateToDateTime(props.minTime, { timezone }),
      onAccept:
        props.onAccept &&
        ((value) => props.onAccept!(value ? dateTimeToDate(value) : null)),
      onChange:
        props.onChange &&
        ((value, context) =>
          props.onChange!(value ? dateTimeToDate(value) : null, context)),
      onError:
        props.onError &&
        ((error, value) =>
          props.onError!(error, value ? dateTimeToDate(value) : null)),
      referenceDate:
        props.referenceDate &&
        dateToDateTime(props.referenceDate, { timezone }),
      shouldDisableTime:
        props.shouldDisableTime &&
        ((value, view) =>
          props.shouldDisableTime!(dateTimeToDate(value), view)),
      timezone,
      value: props.value ? dateToDateTime(props.value, { timezone }) : null,
    };

    return (
      <TimePicker<DateTime>
        {...convertedTimePickerProps}
        ref={ref}
        // Type cast slotProps and slots to work around MUI X typing issue.
        // See: https://github.com/mui/mui-x/issues/9775
        slotProps={{
          textField: {
            showCurrentTimeZone,
            timezoneProp: timezone,
            ...TextFieldProps,
          } as any,
        }}
        slots={{
          textField: TimezoneTextField as any,
        }}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        }}
      />
    );
  },
);

export default LocatedTimePicker;
